import { useState } from 'react';

import { AmountInput } from '@/components/AmountInput';
import { Modal, Notice } from '@/components/Modal';
import { type ClaimItemInput, type MyRelocation, uploadClaimReceipt } from '@/pages/mypage/useWelfare';

/**
 * 귀향여비 정산서 (DOC-68 §5.0 개정 · 사용자 지시 2026-08-27:
 * 「귀향여비도 결재 말고 바로 처리하게 하자」).
 *
 * ## 줄이 표다
 * 그 달에 오간 날짜·구간·교통편·금액을 한 줄씩 적는다. 결재 서식을 거치던 때는 이것을
 * `2026-08-15|서울→평택|버스|24000` 처럼 **파이프로 이어 붙여** 한 칸에 넣었다 — 서식의 정형
 * 항목이 평평한 키-값뿐이어서다. 한 칸이 비면 그 뒤가 통째로 밀리는 모양이었고, 그 오류는
 * 결재가 끝난 뒤에야 드러났다.
 *
 * ## 지급액을 화면이 계산하지 않는다
 * `min(실비, 한도)` 는 서버가 낸다. 화면에는 **합계와 한도를 나란히** 보여 주고 넘치면 그 사실을
 * 미리 적는다 — 넘는 줄 알고 내는 것과 나중에 알게 되는 것은 다르다.
 *
 * ## 줄 하나가 왕복 한 번이고, 영수증은 줄마다 붙는다 (V562)
 * 서버가 줄 수를 월 왕복 횟수(구간표 · 기본 2회)와 맞춰 보고 넘치면 받지 않는다. 특별지역은 횟수를
 * 보지 않고 인사팀이 승인하며 적은 한도만 본다. 한도도 그래서 **판정이 아니라 승인된 신청**에서 읽는다.
 *
 * ## 한 달에 한 번이다
 * 같은 달을 두 번 내면 그 달이 두 번 지급된다(V78 의 유일 인덱스가 막는다). 화면도 이미 낸 달을
 * 고르면 그 자리에서 말한다.
 */
export function ClaimRequestModal({
  eligibility,
  approved,
  claims,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  eligibility: NonNullable<MyRelocation['eligibility']>;
  /** 승인된 귀향여비 신청. 정산의 한도·횟수는 여기 굳은 값이다(특별지역은 인사팀이 적은 금액). */
  approved: MyRelocation['homecomingApplications'][number] | null;
  /** 이미 낸 정산. 같은 달을 다시 고르면 <b>누르기 전에</b> 말한다. */
  claims: MyRelocation['claims'];
  pending: boolean;
  error: string | null;
  onSubmit: (body: { claimMonth: string; items: ClaimItemInput[]; note: string | null }) => void;
  onClose: () => void;
}) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState<ClaimItemInput[]>([blank()]);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const cap = approved?.monthlyCap ?? eligibility.monthlyCap ?? 0;
  const roundTrip = approved ? (approved.roundTripAmount ?? null) : eligibility.roundTripAmount;
  const trips = approved ? (approved.monthlyRoundTrips ?? null) : eligibility.monthlyRoundTrips;
  const special = approved?.specialArea ?? eligibility.specialArea;
  // 서버와 같은 규칙: 횟수가 비면 2회. 특별지역은 횟수를 보지 않는다.
  const allowedTrips = special ? null : (trips ?? 2);
  const total = rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
  const over = cap > 0 && total > cap;
  const already = claims.some((claim) => claim.claimMonth === month);
  const filled = rows.filter((row) => row.travelDate);
  const tooMany = allowedTrips != null && filled.length > allowedTrips;

  const attach = async (index: number, file: File | undefined) => {
    if (!file) return;
    setUploading(index);
    setUploadError(null);
    try {
      const uploaded = await uploadClaimReceipt(file);
      edit(index, { evidenceKey: uploaded.key, evidenceName: uploaded.name });
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(null);
    }
  };

  const edit = (index: number, patch: Partial<ClaimItemInput>) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <Modal title="귀향여비 정산" onClose={onClose} maxWidth="640px" closeDisabled={pending}>
      <div className="flex items-center gap-3 flex-wrap">
        <label style={{ fontSize: '13px' }}>
          <span style={labelText}>정산월</span>
          <input
            className="input-field"
            type="month"
            aria-label="정산월"
            style={inputStyle}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </label>
        <span style={{ fontSize: '13px' }}>
          월 한도 <b>{cap.toLocaleString('ko-KR')}원</b>
          {roundTrip != null && trips != null && (
            <span style={mutedText}>
              {' '}({roundTrip.toLocaleString('ko-KR')}원 × 월 {trips}회)
            </span>
          )}
        </span>
      </div>

      {already && (
        <Notice tone="error">
          {month} 은 이미 정산했습니다. 고칠 것이 있으면 인사팀에 문의해 주세요.
        </Notice>
      )}
      {tooMany && (
        <Notice tone="error">
          월 왕복은 {allowedTrips}회까지입니다. 적은 왕복이 {filled.length}회입니다.
        </Notice>
      )}

      {/* ── 줄 ── */}
      <p style={{ ...labelText, marginTop: '12px' }}>그 달에 오간 내역</p>
      <div className="overflow-x-auto">
        <div style={{ minWidth: 'max-content' }}>
          <div className="flex gap-2" style={{ fontSize: '11px', color: 'var(--text-muted)', paddingBottom: '2px' }}>
            <span style={{ width: '130px' }}>날짜</span>
            <span style={{ width: '150px' }}>왕복 구간</span>
            <span style={{ width: '90px' }}>교통편</span>
            <span style={{ width: '100px' }}>금액</span>
            <span style={{ width: '140px' }}>영수증</span>
            <span style={{ width: '40px' }} />
          </div>
          {rows.map((row, index) => (
            <div key={index} className="flex gap-2 items-center" style={{ paddingBottom: '4px' }}>
              <input
                className="input-field"
                type="date"
                aria-label={`${index + 1}번째 날짜`}
                style={{ ...inputStyle, width: '130px' }}
                value={row.travelDate}
                onChange={(e) => edit(index, { travelDate: e.target.value })}
              />
              <input
                className="input-field"
                aria-label={`${index + 1}번째 구간`}
                placeholder="서울↔평택"
                style={{ ...inputStyle, width: '150px' }}
                value={row.route ?? ''}
                onChange={(e) => edit(index, { route: e.target.value })}
              />
              <input
                className="input-field"
                aria-label={`${index + 1}번째 교통편`}
                placeholder="버스"
                style={{ ...inputStyle, width: '90px' }}
                value={row.transport ?? ''}
                onChange={(e) => edit(index, { transport: e.target.value })}
              />
              <AmountInput
                ariaLabel={`${index + 1}번째 금액`}
                style={{ ...inputStyle, width: '100px' }}
                value={row.amount == null ? '' : String(row.amount)}
                // 치는 도중의 「1234.」 는 Number 가 못 읽는다 — 그때는 이전 값을 지키고 글자만 남긴다.
                onChange={(next) => edit(index, { amount: next === '' ? null : Number.isFinite(Number(next)) ? Number(next) : row.amount })}
              />
              {/* 파일 고르개는 버튼 모양의 label 로 연다 — 기본 file 입력은 칸 폭을 지키지 않는다. */}
              <label
                className="btn-secondary"
                style={{ ...receiptButton, width: '140px' }}
                title={row.evidenceName ?? undefined}
              >
                {uploading === index ? '올리는 중…' : (row.evidenceName ?? '파일 선택')}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  aria-label={`${index + 1}번째 영수증`}
                  style={{ display: 'none' }}
                  disabled={uploading != null}
                  onChange={(e) => {
                    void attach(index, e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
              {/* 마지막 한 줄은 지우지 않는다 — 빈 표가 되면 무엇을 적는 자리인지 사라진다. */}
              <button
                style={{ fontSize: '12px', color: rows.length > 1 ? 'var(--danger)' : 'var(--text-muted)', width: '40px' }}
                disabled={rows.length <= 1}
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
              >
                삭제
              </button>
            </div>
          ))}
        </div>
      </div>
      <button className="btn-secondary" style={compactButton} onClick={() => setRows((prev) => [...prev, blank()])}>
        줄 추가
      </button>

      {/*
        합계와 한도를 <b>나란히</b> 보여 준다. 넘는 줄 알고 내는 것과 나중에 아는 것은 다르다 —
        초과분은 깎아서 감추지 않고 자기부담으로 남는다(V78).
      */}
      <p style={{ fontSize: '13px', marginTop: '10px' }}>
        실비 합계 <b>{total.toLocaleString('ko-KR')}원</b>
        <span style={mutedText}> / 한도 {cap.toLocaleString('ko-KR')}원</span>
        {over && (
          <span style={{ color: 'var(--danger)' }}>
            {' '}— 한도를 {(total - cap).toLocaleString('ko-KR')}원 넘습니다. 넘는 금액은 자기부담입니다.
          </span>
        )}
      </p>

      <label style={{ fontSize: '13px', display: 'block', marginTop: '10px' }}>
        <span style={labelText}>비고</span>
        <input
          className="input-field"
          aria-label="비고"
          style={{ ...inputStyle, width: '100%' }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {uploadError && <Notice tone="error">{uploadError}</Notice>}
      {error && <Notice tone="error">{error}</Notice>}

      <div className="flex items-center gap-2" style={{ marginTop: '14px', justifyContent: 'flex-end' }}>
        <span style={{ ...mutedText, marginRight: 'auto' }}>
          결재를 거치지 않고 <b>인사팀에 바로</b> 갑니다.
        </span>
        <button style={{ fontSize: '13px' }} onClick={onClose} disabled={pending}>취소</button>
        <button
          className="btn-primary"
          style={{ fontSize: '13px', padding: '6px 14px' }}
          disabled={pending || already || filled.length === 0 || uploading != null}
          onClick={() => onSubmit({ claimMonth: month, items: filled, note: note.trim() || null })}
        >
          {pending ? '보내는 중…' : `${filled.length}줄 정산 요청`}
        </button>
      </div>
    </Modal>
  );
}

function blank(): ClaimItemInput {
  return { travelDate: '', route: null, transport: null, amount: null };
}

const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const labelText = { display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '2px' } as const;
const inputStyle = { fontSize: '13px', padding: '4px 6px' } as const;
const compactButton = { fontSize: '12px', padding: '4px 10px', marginTop: '4px' } as const;
const receiptButton = {
  fontSize: '12px',
  padding: '4px 8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
} as const;
